import React from 'react';
import { toast } from 'sonner';
import { Formik } from 'formik';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useProjectOptions } from '@/hooks/useProjectOptions';
import { usePushCapability } from '@/hooks/usePushCapability';
import { InstallPromptModal } from '@/hooks/usePWAInstall';
import { useOptionalFilterState } from '@/hooks/useFilter';
import { useAuthState } from '@/utils/auth';
import { useRegisterWebPush, useWebPushStatus } from '@/utils/WebPushHandler';
import { NotificationsPrompt } from '@/utils/NotificationsPrompt';
import { Link } from 'react-router-dom';
import { isBrave } from '@/utils/isBrave';
import { isMobileApp } from '@/utils/isMobileApp';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MultiselectCombobox } from '@/components/Inputs/MultiselectCombobox';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  PushTopicPreferenceFieldsFragment,
  PushTopicPreferenceInput,
  RoleNames,
  useGetMyNotificationPreferencesQuery,
  useSendTestPushNotificationMutation,
  useSetNotificationOptOutMutation,
  useSetNotificationPreferencesMutation,
} from '@/generated/codegen';

// Elective topic preferences: what the operator can choose to receive.
// The two commented-out entries are deliberately unavailable — see the
// plan's maintenance notes for how to bring one online.
const projectTopics = [
  { label: 'Notificación de nuevo parte', topicName: 'new-report' },
  {
    label: 'Notificación de reporte de novedad',
    topicName: 'new-machine-notice',
  },
  {
    label: 'Checklists con ítems que no pasan',
    topicName: 'checklist-with-findings',
  },
  // { label: 'Partes con observaciones', channelSuffix: 'report-with-obs' },
  // { label: 'Próximos Services', channelSuffix: 'next-service' },
];

const MANAGED_TOPICS = new Set(projectTopics.map((topic) => topic.topicName));

// The daily reminder is a derived audience (role-based), never an elective
// preference — it must never appear in `projectTopics` above. Its control
// is the opt-out toggle below, backed by a different mutation and a
// different storage model (per user, not per device).
const MACHINIST_REMINDER_TOPIC = 'machinist-reminder';

type TopicMode = 'off' | 'all' | 'some';

const isTopicMode = (value: string): value is TopicMode =>
  value === 'off' || value === 'all' || value === 'some';

type ProjectSelection = { label: string; project_id: number };

type TopicFormValues = {
  mode: Record<string, TopicMode>;
  selectedProjects: Record<string, ProjectSelection[]>;
};

const modeForTopic = (
  topicRows: PushTopicPreferenceFieldsFragment[],
  topicName: string,
  companyId: number
): TopicMode => {
  const rows = topicRows.filter(
    (row) => row.topic === topicName && row.company_id === String(companyId)
  );
  if (rows.length === 0) return 'off';
  if (rows.some((row) => row.project_id === null || row.project_id == null))
    return 'all';
  return 'some';
};

const selectedProjectsForTopic = (
  topicRows: PushTopicPreferenceFieldsFragment[],
  topicName: string,
  companyId: number,
  projectLabelById: Map<number, string>
): ProjectSelection[] =>
  topicRows
    .filter(
      (row) =>
        row.topic === topicName &&
        row.company_id === String(companyId) &&
        row.project_id !== null &&
        row.project_id !== undefined
    )
    .map((row) => {
      const project_id = Number(row.project_id);
      return {
        project_id,
        label: projectLabelById.get(project_id) ?? String(project_id),
      };
    });

const TopicPreferencesForm: React.FC<{
  companyId: number;
  currentTopics: PushTopicPreferenceFieldsFragment[];
  projectItems: ProjectSelection[];
  onSaved: () => Promise<unknown>;
}> = ({ companyId, currentTopics, projectItems, onSaved }) => {
  const [setNotificationPreferences] = useSetNotificationPreferencesMutation();
  const [savingTopic, setSavingTopic] = React.useState<string | null>(null);
  const [searchValues, setSearchValues] = React.useState<
    Record<string, string>
  >({});

  const projectLabelById = React.useMemo(
    () => new Map(projectItems.map((item) => [item.project_id, item.label])),
    [projectItems]
  );

  const initialValues = React.useMemo<TopicFormValues>(
    () => ({
      mode: Object.fromEntries(
        projectTopics.map((topic) => [
          topic.topicName,
          modeForTopic(currentTopics, topic.topicName, companyId),
        ])
      ),
      selectedProjects: Object.fromEntries(
        projectTopics.map((topic) => [
          topic.topicName,
          selectedProjectsForTopic(
            currentTopics,
            topic.topicName,
            companyId,
            projectLabelById
          ),
        ])
      ),
    }),
    [currentTopics, companyId, projectLabelById]
  );

  const commit = async (
    topicName: string,
    values: TopicFormValues,
    overrides: Partial<TopicFormValues>
  ) => {
    const merged: TopicFormValues = {
      mode: { ...values.mode, ...overrides.mode },
      selectedProjects: {
        ...values.selectedProjects,
        ...overrides.selectedProjects,
      },
    };

    const managedEntries: PushTopicPreferenceInput[] = projectTopics.flatMap(
      (topic): PushTopicPreferenceInput[] => {
        const mode = merged.mode[topic.topicName];
        if (mode === 'off') return [];
        if (mode === 'all') {
          return [
            {
              topic: topic.topicName,
              company_id: String(companyId),
              project_id: null,
            },
          ];
        }
        return (merged.selectedProjects[topic.topicName] ?? []).map(
          (project) => ({
            topic: topic.topicName,
            company_id: String(companyId),
            project_id: String(project.project_id),
          })
        );
      }
    );

    // Preserve everything this form does not manage: other companies, and
    // (once more elective topics exist) other topics not shown here. Only
    // the managed topics for the currently-selected company are replaced.
    const next: PushTopicPreferenceInput[] = [
      ...currentTopics
        .filter(
          (row) =>
            !(
              MANAGED_TOPICS.has(row.topic) &&
              row.company_id === String(companyId)
            )
        )
        .map((row) => ({
          topic: row.topic,
          company_id: row.company_id,
          project_id: row.project_id,
        })),
      ...managedEntries,
    ];

    setSavingTopic(topicName);
    try {
      await setNotificationPreferences({
        variables: { topics: next },
      });
      return true;
    } catch (error) {
      // D6: the diagnostic used to be discarded entirely here — the real
      // error (e.g. "Not authorized") was visible only in the Network tab,
      // which cost days when debugging BIL-4952. The toast text stays.
      console.error('setNotificationPreferences failed', error);
      toast.error(
        'No se pudo guardar la preferencia de notificaciones. Intentá de nuevo.'
      );
      return false;
    } finally {
      await onSaved();
      setSavingTopic(null);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={() => {}}
    >
      {({ values, setFieldValue }) => (
        <div className="grid gap-6">
          {projectTopics.map((topic) => {
            const mode = values.mode[topic.topicName] ?? 'off';
            const disabled = savingTopic === topic.topicName;

            return (
              <div key={topic.topicName} className="grid gap-4">
                <Label htmlFor={topic.topicName} className="text-base">
                  {topic.label}
                </Label>
                <RadioGroup
                  id={topic.topicName}
                  value={mode}
                  onValueChange={(value) => {
                    if (!isTopicMode(value)) return;
                    setFieldValue(`mode.${topic.topicName}`, value);

                    // D5: "some" with zero projects selected has nothing to
                    // save. Committing it would delete an existing
                    // company-wide row and snap the radio back to "off"
                    // after the refetch. The commit for "some" happens once
                    // the first project is picked (the combobox path below).
                    if (
                      value === 'some' &&
                      (values.selectedProjects[topic.topicName] ?? [])
                        .length === 0
                    ) {
                      return;
                    }

                    const previous = mode;
                    void commit(topic.topicName, values, {
                      mode: { [topic.topicName]: value },
                    }).then((succeeded) => {
                      if (!succeeded) {
                        setFieldValue(`mode.${topic.topicName}`, previous);
                      }
                    });
                  }}
                  disabled={disabled}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="off" id={`${topic.topicName}-off`} />
                    <Label htmlFor={`${topic.topicName}-off`}>
                      No Recibir Notificaciones
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id={`${topic.topicName}-all`} />
                    <Label htmlFor={`${topic.topicName}-all`}>
                      Notificarme para todos los proyectos
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="some"
                      id={`${topic.topicName}-some`}
                    />
                    <Label htmlFor={`${topic.topicName}-some`}>
                      Notificarme para algunos Proyectos:
                    </Label>
                  </div>
                  <MultiselectCombobox
                    items={projectItems}
                    value={values.selectedProjects[topic.topicName] ?? []}
                    onValueChange={(selected) => {
                      const next = selected ?? [];
                      const previous =
                        values.selectedProjects[topic.topicName] ?? [];
                      setFieldValue(
                        `selectedProjects.${topic.topicName}`,
                        next
                      );
                      void commit(topic.topicName, values, {
                        selectedProjects: { [topic.topicName]: next },
                      }).then((succeeded) => {
                        if (!succeeded) {
                          setFieldValue(
                            `selectedProjects.${topic.topicName}`,
                            previous
                          );
                        }
                      });
                    }}
                    searchValue={searchValues[topic.topicName] ?? ''}
                    onSearchValueChange={(search) =>
                      setSearchValues((prev) => ({
                        ...prev,
                        [topic.topicName]: search,
                      }))
                    }
                    getComboboxItem={(item) => ({
                      id: String(item.project_id),
                      label: item.label,
                      value: item,
                    })}
                    loading={disabled}
                    placeholder="Elegí proyectos..."
                    emptyLabel="No hay resultados"
                    disabled={mode !== 'some' || disabled}
                  />
                </RadioGroup>
              </div>
            );
          })}
        </div>
      )}
    </Formik>
  );
};

const MachinistReminderToggle: React.FC<{
  optedOut: boolean;
  onToggle: (optedOut: boolean) => Promise<unknown>;
}> = ({ optedOut, onToggle }) => {
  const [saving, setSaving] = React.useState(false);

  return (
    <>
      <Separator className="my-4" />
      <div className="flex items-center justify-between gap-4">
        <div className="grid gap-1">
          <Label htmlFor="machinist-reminder-toggle" className="text-base">
            Recordatorio diario de parte
          </Label>
          <p className="text-sm text-muted-foreground">
            Se envía las tardes de los días hábiles para avisarte si todavía no
            cargaste tu parte diario.
          </p>
        </div>
        <Switch
          id="machinist-reminder-toggle"
          aria-label="Recordatorio diario de parte"
          checked={!optedOut}
          disabled={saving}
          onCheckedChange={async (checked) => {
            setSaving(true);
            try {
              await onToggle(!checked);
            } finally {
              setSaving(false);
            }
          }}
        />
      </div>
    </>
  );
};

/**
 * iOS has one hard prerequisite no other platform has: the app must live on
 * the Home Screen or there is no Push API at all, in any iOS version. The
 * instructions themselves are not restated here — this opens the app's
 * existing share-sheet explainer so there is only ever one copy of them.
 */
const IosInstallRequired: React.FC = () => {
  const [showInstructions, setShowInstructions] = React.useState(false);

  return (
    <>
      <InstallPromptModal
        shouldPrompt={showInstructions && 'ios'}
        acceptInstall={() => setShowInstructions(false)}
        declineInstall={() => setShowInstructions(false)}
      />
      En iPhone y iPad las notificaciones solo funcionan si agregás Bilderit a
      la pantalla de inicio. Instalá la app y volvé a esta pantalla para
      habilitarlas.
      <br />
      <br />
      <Button variant="success" onClick={() => setShowInstructions(true)}>
        Cómo instalar la app
      </Button>
    </>
  );
};

/**
 * "Is push actually working on this phone?" is the first question an operator
 * or a support person asks, and until now the only way to answer it was to
 * create a real parte. This goes through the same fan-out and encryption path
 * as a production notification, so a delivery here proves the whole send half.
 */
const TestNotificationButton: React.FC = () => {
  const [sendTestPushNotification, { loading }] =
    useSendTestPushNotificationMutation();

  const send = async () => {
    try {
      const { data } = await sendTestPushNotification();
      const devices = data?.sendTestPushNotification ?? 0;
      if (devices === 0) {
        toast.error(
          'No encontramos dispositivos registrados para enviar la prueba.'
        );
        return;
      }
      toast.success(
        `Enviamos una notificación de prueba a ${devices} ${
          devices === 1 ? 'dispositivo' : 'dispositivos'
        }.`
      );
    } catch {
      toast.error(
        'No se pudo enviar la notificación de prueba. Intentá de nuevo.'
      );
    }
  };

  return (
    <>
      <Separator className="my-4" />
      <div className="grid gap-2">
        <p className="text-sm text-muted-foreground">
          Enviá una notificación a tus dispositivos para comprobar que las estás
          recibiendo bien.
        </p>
        <Button
          variant="outline"
          className="justify-self-start"
          disabled={loading}
          onClick={send}
        >
          {loading ? <LoadingSpinner /> : 'Enviar notificación de prueba'}
        </Button>
      </div>
    </>
  );
};

/**
 * Terminal failure of the subscribe flow.
 *
 * Before this existed, `status === 'error'` fell through to the card's final
 * `else` and rendered a **loading spinner** — so a permanent failure looked
 * like "still loading", with no message, no retry, and no way to reach the
 * test button. That is what reviewers reported on Brave and Firefox.
 *
 * Brave gets its own line because it is the one cause we cannot work around
 * from here: Brave routes Web Push through Google's push service and ships
 * with that turned **off**, so `pushManager.subscribe()` rejects with
 * `AbortError: Registration failed - push service error` no matter what we do.
 * Detection is deliberately feature-based (the shared `isBrave` helper), not
 * UA-based — Brave reports itself as Chrome, which is why these failures
 * arrived in Sentry tagged `browser.name: Chrome`.
 *
 * The Brave copy deliberately points at the settings page without naming the
 * exact toggle: its label could not be verified (Brave's docs reject
 * automated fetches), and a confidently wrong instruction is worse than a
 * vague correct one. If you can confirm the label on a real Brave install,
 * make the copy exact.
 */
const PushRegistrationError: React.FC<{ onRetry: () => void }> = ({
  onRetry,
}) => {
  const braveDetected = isBrave();

  return (
    <>
      No pudimos activar las notificaciones en este navegador.
      <br />
      <br />
      {braveDetected ? (
        <>
          Brave bloquea las notificaciones push por defecto. Podés habilitarlas
          desde <code>brave://settings/privacy</code>; después volvé acá y
          reintentá.
        </>
      ) : (
        <>
          Puede ser un problema momentáneo de conexión con el servicio de
          notificaciones del navegador. Si al reintentar sigue fallando,
          comunicate con el soporte técnico de Bilderit.
        </>
      )}
      <br />
      <br />
      <Button variant="outline" onClick={onRetry}>
        Reintentar
      </Button>
    </>
  );
};

export const NotificationSettings: React.FC = () => {
  const filter = useOptionalFilterState();
  const { user } = useAuthState();
  const capability = usePushCapability();
  const status = useWebPushStatus();
  const registerWebPush = useRegisterWebPush();
  const projects = useProjectOptions();
  const [showNotifPrompt, setShowNotifPrompt] = React.useState(false);

  const companyId = filter.query?.company_id;

  const { data, loading, refetch } = useGetMyNotificationPreferencesQuery();
  const [setNotificationOptOut] = useSetNotificationOptOutMutation();

  const currentTopics = data?.getMyNotificationPreferences?.topics ?? [];
  const optedOutTopics =
    data?.getMyNotificationPreferences?.optedOutTopics ?? [];

  const isMachinist =
    user?.roles?.some((role) => role?.role === RoleNames.Machinist) ?? false;

  const projectItems: ProjectSelection[] = projects
    .filter(
      (option): option is typeof option & { value: { project_id: number } } =>
        Boolean(option.value.project_id)
    )
    .map((option) => ({
      project_id: option.value.project_id,
      label: option.label,
    }));

  const handleReminderToggle = async (optedOut: boolean) => {
    try {
      await setNotificationOptOut({
        variables: { topic: MACHINIST_REMINDER_TOPIC, optedOut },
      });
    } catch {
      toast.error('No se pudo guardar el cambio. Intentá de nuevo.');
    } finally {
      await refetch();
    }
  };

  return (
    <Card id="notifications">
      <CardHeader>
        <CardTitle>Notificaciones</CardTitle>
        <CardDescription>
          Configura notificaciones para recibir alertas directamente en este
          dispositivo.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* A capability problem outranks every permission state: there is no
            point offering to enable notifications on a device that cannot
            receive them, and on iOS the fix is the user's to apply. */}
        {capability === 'needs-install' ? (
          <IosInstallRequired />
        ) : capability === 'needs-ios-update' ? (
          <>
            Para recibir notificaciones en este dispositivo necesitás iOS 16.4 o
            más reciente. <br />
            Actualizá el sistema desde Ajustes &gt; General &gt; Actualización
            de software y volvé a esta pantalla.
          </>
        ) : capability === 'unsupported' ? (
          <>
            Este navegador no admite notificaciones push. <br />
            Comunicate con el soporte técnico de Bilderit si necesitás recibir
            notificaciones en este dispositivo.
          </>
        ) : status === 'prompt-required' ? (
          <>
            <NotificationsPrompt
              shouldPrompt={showNotifPrompt}
              toggle={() => setShowNotifPrompt(false)}
            />
            Las notificaciones deben ser habilitadas antes de poder acceder a
            esta configuracion. Habilita las notificaciones en tu dispositivo
            para recibir recordatorios y novedades de tus equipos y proyectos.
            <br />
            <br />
            <Button variant="success" onClick={() => setShowNotifPrompt(true)}>
              Habilitar notificaciones
            </Button>
          </>
        ) : status === 'denied' ? (
          <>
            Las notificaciones fueron deshabilitadas en tu navegador. <br />
            Comunicate con tu administrador o con el soporte técnico de Bilderit
            para volver a habilitarlas.
          </>
        ) : // P6 (plan 020): preferences are per-user, so in principle this
        // form could be shown from any logged-in session — e.g. desktop
        // Safari configuring what reaches the phone. Deliberately deferred:
        // unlocking that is a product/UX decision with its own empty-state
        // copy, so this surface stays gated behind a push-capable,
        // subscribed session.
        status === 'subscribed' ? (
          <>
            {!companyId ? (
              <>
                Las notificaciones ya están configuradas para este dispositivo!.
                {!isMobileApp() && (
                  <>
                    <br />
                    Para configurar qué notificaciones querés recibir, hacé
                    click{' '}
                    <Link
                      to={{
                        pathname: '/choose_company',
                        state: { stay: true },
                      }}
                    >
                      acá
                    </Link>{' '}
                    para seleccionar tu compañia primero.
                  </>
                )}
              </>
            ) : loading ? (
              <div className="flex items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <TopicPreferencesForm
                companyId={companyId}
                currentTopics={currentTopics}
                projectItems={projectItems}
                onSaved={refetch}
              />
            )}
            {/*
              Deliberately OUTSIDE the `companyId` branch above. This opt-out is
              per *user*, not per company — `setNotificationOptOut` takes only a
              topic. Machinists work in the maquinista app, which has no company
              picker at all and never has a company selected, so gating this on
              `companyId` made the control unreachable for the entire audience it
              was built for. It still waits on the subscription query, because
              `optedOutTopics` defaults to `[]` and would otherwise flash "on"
              before the real value arrives.
            */}
            {isMachinist && !loading && (
              <MachinistReminderToggle
                optedOut={optedOutTopics.includes(MACHINIST_REMINDER_TOPIC)}
                onToggle={handleReminderToggle}
              />
            )}
          </>
        ) : status === 'error' ? (
          <PushRegistrationError onRetry={registerWebPush} />
        ) : (
          <div className="flex items-center justify-center">
            <LoadingSpinner />
          </div>
        )}
        {capability === 'available' && status === 'subscribed' && (
          <TestNotificationButton />
        )}
      </CardContent>
    </Card>
  );
};
